import { Box, Theme, Typography } from "@mui/material";
import { Order, PaginationObj, SortingObj, TableHeaderData } from "../../types";
import CustomTable from "../../common/customTable";
import { FetchFundHousePerformanceDataArgs, FundHousePerformanceObj, PerformersObj } from "../types";
import FundsDataTable from "./FundDataTable";
import PerformersDataTable from "./PerformersDataTable";
import { NO_DATA_AVAILABLE } from "../../../../utils/constant";
import { useHistory } from "react-router";

const fundHouseTableHeaderData: TableHeaderData[] = [
    {
        id: 'customerName',
        label: 'CLIENT NAME',
        align: 'left',
        isSortable: true,
        headerTableCellStyle: { minWidth: "100px" }
    },
    {
        id: 'securityName',
        label: 'SCHEME NAME',
        align: 'center',
        isSortable: true,
    },
    {
        id: 'issuerName',
        label: 'FUND HOUSE',
        align: 'center',
        isSortable: true,
    },
    {
        id: 'investedValue',
        label: 'INVESTED VALUE',
        align: 'center',
        isSortable: true,
    },
    {
        id: 'marketValue',
        label: 'MARKET VALUE',
        align: 'center',
        isSortable: true,
    },
    {
        id: 'unrelGainLoss',
        label: 'UNREALISED GAIN/LOSS',
        align: 'center',
        isSortable: true,
    },
]

const performersTableHeaderData: TableHeaderData[] = [
    {
        id: 'strategy',
        label: 'STRATEGY',
        align: 'left',
        isSortable: false,
        headerTableCellStyle: { minWidth: "590px" }
    },
    {
        id: 'category',
        label: 'CATEGORY',
        align: 'left',
        isSortable: false,
    },
    {
        id: 'fundHouse',
        label: 'FUND HOUSE',
        align: 'center',
        isSortable: false,
    },
    {
        id: 'return',
        label: 'RETURN',
        align: 'center',
        isSortable: false,
    },
    {
        id: 'exposure',
        label: 'EXPOSURE (%)',
        align: 'center',
        isSortable: false,
    },
]

interface PMSOverviewProps {
    isLoading: boolean;
    fundHousePerformanceTableSortingObj: SortingObj;
    setFundHousePerformanceTableSortingObj: React.Dispatch<React.SetStateAction<SortingObj>>;
    fetchFundHousePerformanceData: ({
        sortItem,
        order,
        currentPage,
        rowsPerPage
    }: FetchFundHousePerformanceDataArgs) => void;
    fundHousePerformanceTablePaginationObj: PaginationObj;
    setFundHousePerformanceTablePaginationObj: React.Dispatch<React.SetStateAction<PaginationObj>>;
    isFundHousePerformanceTableDataLoading: boolean;
    fundHousePerformanceTableData: FundHousePerformanceObj;
    isTopFivePerformersDataLoading: boolean;
    topFivePerformersData: PerformersObj
    isBottomFivePerformersDataLoading: boolean;
    bottomFivePerformersData: PerformersObj;
}

export default function PMSOverview({
    isLoading,
    fundHousePerformanceTableSortingObj,
    setFundHousePerformanceTableSortingObj,
    fetchFundHousePerformanceData,
    fundHousePerformanceTablePaginationObj,
    setFundHousePerformanceTablePaginationObj,
    isFundHousePerformanceTableDataLoading,
    fundHousePerformanceTableData,
    isTopFivePerformersDataLoading,
    topFivePerformersData,
    isBottomFivePerformersDataLoading,
    bottomFivePerformersData
}: PMSOverviewProps): JSX.Element {
    const history = useHistory();
    const pathName = history?.location?.pathname;
    const initialRouter = (pathName ?? '').split('/').filter((item) => item !== ''); // CORR-61: guard undefined pathname
    const isInvestorProfilePage = `${initialRouter[0] ?? ""}`.toLowerCase() === "investor-profile" || `${initialRouter[0] ?? ""}`.toLowerCase() === "investor-dashboard";
    const filteredTableHeaderData = fundHouseTableHeaderData.filter(
        (header) => !(isInvestorProfilePage && (header.id === "customerName" || header.id === "securityName"))
    );

    const handleSort = (orderBy: string, order: Order) => {
        setFundHousePerformanceTableSortingObj({
            order,
            orderBy
        })

        fetchFundHousePerformanceData({
            sortItem: orderBy,
            order,
            currentPage: fundHousePerformanceTablePaginationObj.currentPage + 1,
            rowsPerPage: fundHousePerformanceTablePaginationObj.rowsPerPage
        })
    }

    const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFundHousePerformanceTablePaginationObj(prevState => ({
            ...prevState,
            rowsPerPage: Number(event.target.value),
            currentPage: 0,
        }))

        fetchFundHousePerformanceData({
            rowsPerPage: Number(event.target.value),
            sortItem: fundHousePerformanceTableSortingObj.orderBy,
            order: fundHousePerformanceTableSortingObj.order,
        })
    }

    const handlePageChange = (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
        setFundHousePerformanceTablePaginationObj(prevState => ({
            ...prevState,
            currentPage: page
        }))

        fetchFundHousePerformanceData({
            currentPage: page + 1,
            sortItem: fundHousePerformanceTableSortingObj.orderBy,
            order: fundHousePerformanceTableSortingObj.order,
            rowsPerPage: fundHousePerformanceTablePaginationObj.rowsPerPage
        })
    }

    return (
        <Box>
            <Box
                sx={(theme: Theme) => ({
                    padding: "35px",
                    borderRadius: "25px",
                    backgroundColor: theme.palette.common.white,
                    boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`,
                    marginBottom: "15px"
                })}
            >
                {(!(isFundHousePerformanceTableDataLoading || isLoading) && !fundHousePerformanceTableData.isSuccess) ? (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            height: "100%",
                        }}
                    >
                        <Typography
                            variant="body_600"
                            sx={(theme: Theme) => ({
                                fontWeight: 500,
                                lineHeight: "22.5px",
                                letterSpacing: "0.01px",
                                color: theme.palette.text.primary
                            })}
                        >
                            {NO_DATA_AVAILABLE}
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <Typography
                            variant="h2"
                            sx={(theme: Theme) => ({
                                lineHeight: "25px",
                                letterSpacing: "0.01px",
                                color: theme.palette.text.primary
                            })}
                        >
                            Fund House Performance
                        </Typography>
                        <Box sx={{ marginTop: "25px" }}>
                            <CustomTable
                                isLoading={isFundHousePerformanceTableDataLoading || isLoading}
                                tableBody={
                                    <FundsDataTable data={fundHousePerformanceTableData.data} />
                                }
                                tableData={fundHousePerformanceTableData.data}
                                tableHeaderData={filteredTableHeaderData}
                                handleSort={handleSort}
                                tableDataCount={fundHousePerformanceTablePaginationObj.totalRows}
                                rowsPerPage={fundHousePerformanceTablePaginationObj.rowsPerPage}
                                rowsPerPageOptions={[5, 10, 15, 20]}
                                handleRowsPerPageChange={handleRowsPerPageChange}
                                handlePageChange={handlePageChange}
                                currentPage={fundHousePerformanceTablePaginationObj.currentPage}
                                order={fundHousePerformanceTableSortingObj.order}
                                orderBy={fundHousePerformanceTableSortingObj.orderBy}
                                tableBodyLoaderRowCustomStyles={{
                                    '& td:first-of-type': {
                                        paddingLeft: "0px"
                                    },
                                }}
                                tableHeadRowCustomStyles={{
                                    '& th:first-of-type': {
                                        paddingLeft: "0px"
                                    }
                                }}
                                tableBodyCustomStyles={{
                                    '& tr:last-child td': {
                                        border: 0
                                    }
                                }}
                                loadingRowsNum={5}
                            />
                        </Box>
                    </>
                )}
            </Box>
            {/* <Box
                sx={(theme: Theme) => ({
                    padding: "35px",
                    borderRadius: "25px",
                    backgroundColor: theme.palette.common.white,
                    boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`,
                    marginBottom: "15px"
                })}
            >
                {(!(isTopFivePerformersDataLoading || isLoading) && !topFivePerformersData.isSuccess) ? (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            height: "100%",
                        }}
                    >
                        <Typography
                            variant="body_600"
                            sx={(theme: Theme) => ({
                                fontWeight: 500,
                                lineHeight: "22.5px",
                                letterSpacing: "0.01px",
                                color: theme.palette.text.primary
                            })}
                        >
                            {NO_DATA_AVAILABLE}
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <Typography
                            variant="h2"
                            sx={(theme: Theme) => ({
                                lineHeight: "25px",
                                letterSpacing: "0.01px",
                                color: theme.palette.text.primary
                            })}
                        >
                            Top 5 Performers
                        </Typography>
                        <Box sx={{ marginTop: "25px" }}>
                            <CustomTable
                                isLoading={isTopFivePerformersDataLoading || isLoading}
                                tableBody={
                                    <PerformersDataTable data={topFivePerformersData.data} />
                                }
                                tableData={topFivePerformersData.data}
                                tableHeaderData={performersTableHeaderData}
                                isPaginationVisible={false}
                                tableDataCount={topFivePerformersData.data.length}
                                tableBodyLoaderRowCustomStyles={{
                                    '& td:first-of-type': {
                                        paddingLeft: "0px"
                                    },
                                }}
                                tableHeadRowCustomStyles={{
                                    '& th:first-of-type': {
                                        paddingLeft: "0px"
                                    }
                                }}
                                tableBodyCustomStyles={{
                                    '& tr:last-child td': {
                                        border: 0
                                    }
                                }}
                                loadingRowsNum={5}
                            />
                        </Box>
                    </>
                )}
            </Box>
            <Box
                sx={(theme: Theme) => ({
                    padding: "35px",
                    borderRadius: "25px",
                    backgroundColor: theme.palette.common.white,
                    boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`
                })}
            >
                {(!(isBottomFivePerformersDataLoading || isLoading) && !bottomFivePerformersData.isSuccess) ? (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            height: "100%",
                        }}
                    >
                        <Typography
                            variant="body_600"
                            sx={(theme: Theme) => ({
                                fontWeight: 500,
                                lineHeight: "22.5px",
                                letterSpacing: "0.01px",
                                color: theme.palette.text.primary
                            })}
                        >
                            {NO_DATA_AVAILABLE}
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <Typography
                            variant="h2"
                            sx={(theme: Theme) => ({
                                lineHeight: "25px",
                                letterSpacing: "0.01px",
                                color: theme.palette.text.primary
                            })}
                        >
                            Bottom 5 Performers
                        </Typography>
                        <Box sx={{ marginTop: "25px" }}>
                            <CustomTable
                                isLoading={isBottomFivePerformersDataLoading || isLoading}
                                tableBody={
                                    <PerformersDataTable data={bottomFivePerformersData.data} />
                                }
                                tableData={bottomFivePerformersData.data}
                                tableHeaderData={performersTableHeaderData}
                                isPaginationVisible={false}
                                tableDataCount={bottomFivePerformersData.data.length}
                                tableBodyLoaderRowCustomStyles={{
                                    '& td:first-of-type': {
                                        paddingLeft: "0px"
                                    },
                                }}
                                tableHeadRowCustomStyles={{
                                    '& th:first-of-type': {
                                        paddingLeft: "0px"
                                    }
                                }}
                                tableBodyCustomStyles={{
                                    '& tr:last-child td': {
                                        border: 0
                                    }
                                }}
                                loadingRowsNum={5}
                            />
                        </Box>
                    </>
                )}
            </Box> */}
        </Box>
    )
}